import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { createPortal } from "react-dom";

import avatar1 from "../../assets/images/users/avatar-1.jpg";

const ProfileDropdown = () => {

    const navigate = useNavigate();

    const profiledropdownData = createSelector(
        (state) => state.Profile,
        (user) => user.user
    );

    const user = useSelector(profiledropdownData);

    const [userName, setUserName] = useState("Admin");
    const [isProfileDropdown, setIsProfileDropdown] = useState(false);

    const profileButtonRef = useRef(null);
    const dropdownRef = useRef(null);

    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        right: 10
    });


    // =========================================================
    // GET USER NAME
    // =========================================================

    useEffect(() => {

        const authUserRaw =
            sessionStorage.getItem("authUser") ||
            localStorage.getItem("authUser");

        let authUser = null;

        if (authUserRaw) {
            try {
                authUser = JSON.parse(authUserRaw);
            } catch (error) {
                authUser = null;
            }
        }

        const authName =
            authUser?.user?.first_name ||
            authUser?.user?.name ||
            authUser?.user?.username ||
            authUser?.data?.first_name ||
            authUser?.data?.name ||
            authUser?.first_name ||
            authUser?.name ||
            authUser?.username ||
            authUser?.email ||
            authUser?.user?.email;

        const profileName =
            user?.first_name ||
            user?.username ||
            user?.name;

        setUserName(
            authName ||
            profileName ||
            "Admin"
        );

    }, [user]);


    // =========================================================
    // CALCULATE DROPDOWN POSITION
    // =========================================================

    const updateDropdownPosition = () => {

        if (!profileButtonRef.current) {
            return;
        }

        const rect =
            profileButtonRef.current.getBoundingClientRect();

        setDropdownPosition({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right
        });
    };


    // =========================================================
    // OPEN / CLOSE DROPDOWN
    // =========================================================

    const toggleProfileDropdown = (event) => {

        event.preventDefault();
        event.stopPropagation();

        updateDropdownPosition();

        setIsProfileDropdown((prev) => !prev);
    };


    // =========================================================
    // UPDATE POSITION ON RESIZE / SCROLL
    // =========================================================

    useEffect(() => {

        if (!isProfileDropdown) {
            return;
        }

        const handlePositionChange = () => {
            updateDropdownPosition();
        };

        window.addEventListener(
            "resize",
            handlePositionChange
        );

        window.addEventListener(
            "scroll",
            handlePositionChange,
            true
        );

        return () => {

            window.removeEventListener(
                "resize",
                handlePositionChange
            );

            window.removeEventListener(
                "scroll",
                handlePositionChange,
                true
            );
        };

    }, [isProfileDropdown]);


    // =========================================================
    // CLOSE WHEN CLICKING OUTSIDE
    // =========================================================

    useEffect(() => {

        if (!isProfileDropdown) {
            return;
        }

        const handleOutsideClick = (event) => {

            const button =
                profileButtonRef.current;

            const dropdown =
                dropdownRef.current;

            if (
                button &&
                !button.contains(event.target) &&
                dropdown &&
                !dropdown.contains(event.target)
            ) {
                setIsProfileDropdown(false);
            }
        };


        document.addEventListener(
            "mousedown",
            handleOutsideClick
        );

        document.addEventListener(
            "touchstart",
            handleOutsideClick,
            { passive: true }
        );


        return () => {

            document.removeEventListener(
                "mousedown",
                handleOutsideClick
            );

            document.removeEventListener(
                "touchstart",
                handleOutsideClick
            );

        };

    }, [isProfileDropdown]);


    // =========================================================
    // PROFILE
    // =========================================================

    const handleProfile = (event) => {

        event.preventDefault();
        event.stopPropagation();

        setIsProfileDropdown(false);

        navigate("/profile");
    };


    // =========================================================
    // SETTINGS
    // =========================================================

    const handleSettings = (event) => {

        event.preventDefault();
        event.stopPropagation();

        setIsProfileDropdown(false);

        navigate("/pages-profile-settings");
    };


    // =========================================================
    // LOGOUT
    // =========================================================

    const handleLogout = (event) => {

        event.preventDefault();
        event.stopPropagation();

        /*
         * Logout ONLY when the user manually
         * clicks Logout.
         */

        sessionStorage.removeItem("authUser");
        localStorage.removeItem("authUser");

        setIsProfileDropdown(false);

        navigate("/login", {
            replace: true
        });
    };


    // =========================================================
    // DROPDOWN UI
    // =========================================================

    const profileMenu = isProfileDropdown
        ? createPortal(

            <div
                ref={dropdownRef}
                className="profile-dropdown-mobile-safe"
                style={{
                    position: "fixed",
                    top: `${dropdownPosition.top}px`,
                    right: `${dropdownPosition.right}px`,
                    width: "215px",
                    background: "#ffffff",
                    borderRadius: "12px",
                    boxShadow:
                        "0 8px 30px rgba(0,0,0,0.18)",
                    border:
                        "1px solid rgba(0,0,0,0.08)",
                    zIndex: 999999,
                    overflow: "hidden"
                }}
                onClick={(event) => {
                    event.stopPropagation();
                }}
                onTouchStart={(event) => {
                    event.stopPropagation();
                }}
            >

                {/* HEADER */}

                <div
                    style={{
                        padding: "16px",
                        display: "flex",
                        alignItems: "center",
                        borderBottom:
                            "1px solid #eeeeee"
                    }}
                >

                    <img
                        src={avatar1}
                        alt="Profile"
                        style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            marginRight: "12px"
                        }}
                    />

                    <div>

                        <div
                            style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#212529"
                            }}
                        >
                            {userName}
                        </div>

                        <div
                            style={{
                                fontSize: "12px",
                                color: "#8791a5",
                                marginTop: "3px"
                            }}
                        >
                            Founder
                        </div>

                    </div>

                </div>


                {/* PROFILE */}

                <button
                    type="button"
                    onClick={handleProfile}
                    onTouchEnd={handleProfile}
                    style={{
                        width: "100%",
                        border: "none",
                        background: "#ffffff",
                        padding: "13px 16px",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "left",
                        cursor: "pointer",
                        color: "#212529"
                    }}
                >

                    <i
                        className="mdi mdi-account-circle"
                        style={{
                            fontSize: "18px",
                            color: "#6c757d",
                            marginRight: "12px"
                        }}
                    />

                    <span>
                        Profile
                    </span>

                </button>


                {/* SETTINGS */}

                <button
                    type="button"
                    onClick={handleSettings}
                    onTouchEnd={handleSettings}
                    style={{
                        width: "100%",
                        border: "none",
                        background: "#ffffff",
                        padding: "13px 16px",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "left",
                        cursor: "pointer",
                        color: "#212529"
                    }}
                >

                    <i
                        className="mdi mdi-cog-outline"
                        style={{
                            fontSize: "18px",
                            color: "#6c757d",
                            marginRight: "12px"
                        }}
                    />

                    <span>
                        Settings
                    </span>

                </button>


                {/* DIVIDER */}

                <div
                    style={{
                        height: "1px",
                        background: "#eeeeee"
                    }}
                />


                {/* LOGOUT */}

                <button
                    type="button"
                    onClick={handleLogout}
                    onTouchEnd={handleLogout}
                    style={{
                        width: "100%",
                        border: "none",
                        background: "#ffffff",
                        padding: "13px 16px",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "left",
                        cursor: "pointer",
                        color: "#f44336"
                    }}
                >

                    <i
                        className="mdi mdi-logout"
                        style={{
                            fontSize: "18px",
                            color: "#f44336",
                            marginRight: "12px"
                        }}
                    />

                    <span>
                        Logout
                    </span>

                </button>

            </div>,

            document.body
        )
        : null;


    // =========================================================
    // MAIN BUTTON
    // =========================================================

    return (
        <>

            <button
                ref={profileButtonRef}
                type="button"
                onClick={toggleProfileDropdown}
                onTouchEnd={(event) => {

                    event.preventDefault();
                    event.stopPropagation();

                    updateDropdownPosition();

                    setIsProfileDropdown(
                        (prev) => !prev
                    );

                }}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: "0",
                    margin: "0",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    touchAction: "manipulation"
                }}
                aria-label="Open profile menu"
            >

                <span
                    className="d-flex align-items-center"
                >

                    <img
                        className="rounded-circle header-profile-user"
                        src={avatar1}
                        alt="Header Avatar"
                    />

                    <span
                        className="text-start ms-xl-2"
                    >

                        <span
                            className="
                                d-none
                                d-xl-inline-block
                                ms-1
                                fw-medium
                                user-name-text
                            "
                        >
                            {userName}
                        </span>

                        <span
                            className="
                                d-none
                                d-xl-block
                                ms-1
                                fs-12
                                text-muted
                                user-name-sub-text
                            "
                        >
                            Founder
                        </span>

                    </span>

                </span>

            </button>


            {profileMenu}

        </>
    );
};

export default ProfileDropdown;